import React, { useState } from 'react';
import { X, User, Briefcase, AlertCircle } from 'lucide-react';
import { Job } from '../../../data/mockJobs';
import BasicInfo from './BasicInfo';
import JobSelection from './JobSelection';
import ProgressIndicator from './ProgressIndicator';
import { candidatesApi, CandidateCreateDTO } from '../../../api/candidates';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useDataRefresh } from '../../../contexts/DataRefreshContext';

interface AddCandidateModalProps {
  onClose: () => void;
  jobs: Job[];
  onCandidateCreated?: (newCandidate: any) => void;
  preSelectedJobId?: string;
}

const AddCandidateModal = ({ onClose, jobs, onCandidateCreated, preSelectedJobId }: AddCandidateModalProps) => {
  const { t } = useLanguage();
  const { refreshCandidates } = useDataRefresh();
  const [formData, setFormData] = useState({
    basicInfo: {
      name: '',
      email: '',
      phone: '',
    },
    selectedJob: preSelectedJobId || '',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const steps = [
    t('candidates.addCandidateModal.steps.position'), 
    t('candidates.addCandidateModal.steps.details')
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.selectedJob) {
      setError(t('candidates.addCandidateModal.validation.selectJob'));
      return;
    }
    
    if (!formData.basicInfo.name || !formData.basicInfo.email) {
      setError(t('candidates.addCandidateModal.validation.nameEmailRequired'));
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare candidate data according to API format
      const candidateData: CandidateCreateDTO = {
        jobId: formData.selectedJob,
        full_name: formData.basicInfo.name,
        email: formData.basicInfo.email,
        phone: formData.basicInfo.phone || undefined,
        status: 'pending', // Default status for new candidates
      };
      
      // Call the API to create the candidate
      const result = await candidatesApi.create(candidateData);
      console.log('Candidate created:', result);
      
      // Refresh the candidates data globally
      refreshCandidates();
      
      // Notify parent component about the new candidate
      if (onCandidateCreated) {
        onCandidateCreated(result);
      }
      
      // Close the modal on success
      onClose();
    } catch (err) {
      console.error('Failed to create candidate:', err);
      setError(t('candidates.addCandidateModal.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <JobSelection
            jobs={jobs}
            selectedJob={formData.selectedJob}
            onChange={(jobId) => setFormData({ ...formData, selectedJob: jobId })}
          />
        );
      case 1:
        return (
          <BasicInfo
            data={formData.basicInfo}
            onChange={(data) => setFormData({ ...formData, basicInfo: data })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg flex items-center justify-center transform rotate-45">
              <div className="transform -rotate-45">
                {currentStep === 0 ? (
                  <Briefcase className="w-6 h-6 text-white" />
                ) : (
                  <User className="w-6 h-6 text-white" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="pt-12 px-6 pb-4">
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">{t('candidates.addCandidateModal.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 0
                ? t('candidates.addCandidateModal.positionPrompt')
                : t('candidates.addCandidateModal.detailsPrompt')}
            </p>
          </div>

          <div className="overflow-x-auto mb-2">
            <ProgressIndicator steps={steps} currentStep={currentStep} />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 pb-4 overflow-y-auto flex-1">
            <div className="bg-gray-50 rounded-xl p-4">
              {renderStep()}
              
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center gap-3 p-4 px-6 border-t bg-gray-50 mt-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <div className="flex gap-3">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {t('common.back')}
                </button>
              )}
              {currentStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('common.continue')}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.submitting')}
                    </>
                  ) : (
                    t('candidates.addCandidate')
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCandidateModal;